import React, { useState, useMemo } from 'react';
import EquipmentLoader from '../loaders/EquipmentLoader';
import PointTypeLoader from '../loaders/PointTypeLoader';
import EquipTypeLoader from '../loaders/EquipTypeLoader';

export const SelectorLoader = ({ buildings }) => (
  <>
    <EquipTypeLoader />
    <PointTypeLoader />
    {buildings?.map((bid) => <EquipmentLoader key={bid} buildingId={bid} />) ?? null}
  </>
);

const FIELDS = ['equipment_types', 'point_types', 'equipment'];

const defaults = (val) =>
  FIELDS.reduce((o, f) => {
    // eslint-disable-next-line no-param-reassign
    o[f] = val;
    return o;
  }, {});

/**
 * A live updating selection handler, used on the export page.
 *
 * The idea is to run lots of selection queries as the user adds constraints,
 * and to then limit further constraints so that there are results.
 *
 * For example, once you pick a building you can only select equipment types
 * that are present in that building
 */
export const useLiveSelector = ({ selectPoints }) => {
  // cache of previously run queries
  const [selections, setSelections] = useState({});
  // { 'equipment': bool }
  const [loading, setLoading] = useState(defaults(false));
  // { 'equipment': (e) => bool }
  const [filters, setFilters] = useState(defaults(() => true));
  const [counts, setCounts] = useState({});

  const onChange = useMemo(
    () => (selector) => {
      // in-place state update helper function
      const update = (setter, key, value) =>
        setter((current) => {
          if (current[key] === value) return current;
          return { ...current, [key]: value };
        });

      const updateFilter = (field, res) => {
        update(setLoading, field, false);
        const fieldMatches = new Set(res[field]);
        update(setCounts, field, fieldMatches.size);
        update(setFilters, field, (val) => fieldMatches.has(val.id));
      };

      // for each field, run a selection using the other fields and with that field unconstrained
      // this will return all possible values for the field given the rest of the selection
      // the field in question needs to be unconstrained to allow for widening of the overall selection
      FIELDS.forEach((field) => {
        const fieldUnconstrained = { ...selector, [field]: [] };
        const key = JSON.stringify(fieldUnconstrained);
        if (selections[key] != null) {
          updateFilter(field, selections[key]);
        } else {
          update(setLoading, field, true);
          selectPoints(fieldUnconstrained).then((res) => {
            update(setSelections, key, res);
            updateFilter(field, res);
          });
        }
      });
    },
    [selectPoints, selections, setSelections, setLoading, setFilters, setCounts]
  );

  return [loading, filters, counts, onChange];
};
